import type { CobaltTableSortDirection } from '@cae/cobalt-react/components/table';

import { CobaltPagination } from '@cae/cobalt-react/components/pagination';
import {
  CobaltTable,
  CobaltTableBody,
  CobaltTableCell,
  CobaltTableContainer,
  CobaltTableHead,
  CobaltTableRow,
  CobaltTableSortLabel,
} from '@cae/cobalt-react/components/table';
import { CobaltIconCautionMajorSolid } from '@cae/cobalt-react/icons/solid/CobaltIconCautionMajorSolid';
import { CobaltIconWarningMajorSolid } from '@cae/cobalt-react/icons/solid/CobaltIconWarningMajorSolid';
import { useHasPermission } from '@contexts/permissions-provider/hooks/use-has-permission/useHasPermission';
import { useInvoicesData } from '@features/invoices/contexts/data/useInvoicesData';
import { useInvoiceNavigation } from '@features/invoices/hooks/use-invoice-navigation/useInvoiceNavigation';
import {
  formatPilotName,
  getDueDateStatus,
  shouldShowDueDateIcon,
  DUE_DATE_STATUS,
} from '@features/invoices/utils/invoices.utils';
import { DataValue } from '@shared/components/data-value/DataValue';
import { formatDateShort } from '@shared/utils/format-date/formatDate';
import * as React from 'react';

import type { InvoiceSortField } from './InvoicesTable.types';

import { InvoiceDownloadCell } from '../../invoice-download-cell/InvoiceDownloadCell';
import { InvoiceNumberCell } from '../../invoice-number-cell/InvoiceNumberCell';
import { OutstandingAmountCell } from '../../outstanding-amount-cell/OutstandingAmountCell';
import { TruncatedList } from '../../truncated-list/TruncatedList';
import styles from './InvoicesTable.module.css';
import {
  toApiSortParam,
  fromApiSortParam,
  TABLE_COLUMNS,
} from './InvoicesTable.utils';

const InvoicesTable = React.memo((): React.ReactElement => {
  const {
    invoices,
    sort,
    handleSortChange,
    pageIndex,
    pageSize,
    handlePageIndexChange,
    handlePageSizeChange,
  } = useInvoicesData();

  const { navigateToInvoice } = useInvoiceNavigation();
  const canDownload = useHasPermission('Invoices_Open_Org');

  const visibleColumns = React.useMemo(
    () =>
      canDownload
        ? TABLE_COLUMNS
        : TABLE_COLUMNS.filter(column => column.id !== 'download'),
    [canDownload]
  );

  const handleRowClick = React.useCallback(
    (id: string): void => {
      navigateToInvoice(id);
    },
    [navigateToInvoice]
  );

  const currentSort = React.useMemo(() => fromApiSortParam(sort), [sort]);

  const handleSortDirectionChange = React.useCallback(
    (column: InvoiceSortField, direction: CobaltTableSortDirection): void => {
      handleSortChange(toApiSortParam(column, direction));
    },
    [handleSortChange]
  );

  const getSortDirection = React.useCallback(
    (columnId: InvoiceSortField): CobaltTableSortDirection =>
      currentSort.column === columnId ? currentSort.direction : false,
    [currentSort]
  );

  return (
    <CobaltTableContainer
      pagination={
        <CobaltPagination
          length={invoices.data.total}
          pageIndex={pageIndex}
          pageSize={pageSize}
          onPageIndexChange={handlePageIndexChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      }
      className={styles.invoicesTable}
      data-testid="invoices-table"
    >
      <CobaltTable size="x-large">
        <CobaltTableHead>
          <CobaltTableRow>
            {visibleColumns.map(column => {
              const sortDirection = getSortDirection(column.id);
              return (
                <CobaltTableCell
                  key={column.id}
                  header
                  aria-label={column.ariaLabel}
                  style={{ minWidth: column.width }}
                >
                  {column.sortable === false ? (
                    column.label
                  ) : (
                    <CobaltTableSortLabel
                      active={sortDirection !== false}
                      direction={sortDirection}
                      onDirectionChange={dir =>
                        handleSortDirectionChange(column.id, dir)
                      }
                    >
                      {column.label}
                    </CobaltTableSortLabel>
                  )}
                </CobaltTableCell>
              );
            })}
          </CobaltTableRow>
        </CobaltTableHead>
        <CobaltTableBody>
          {invoices.data.elements.map(item => {
            const dueDateStatus = getDueDateStatus(item.dueDate);
            const issuedOnFormatted = formatDateShort(item.issuedOn);
            const dueDateFormatted = formatDateShort(item.dueDate);
            return (
              <CobaltTableRow
                key={item.accountReceivableId}
                className={styles.tableRow}
                interactive
                onClick={() => handleRowClick(item.accountReceivableId)}
              >
                <CobaltTableCell>
                  {item.transactionNumber ? (
                    <InvoiceNumberCell value={item.transactionNumber} />
                  ) : (
                    <DataValue value={null} />
                  )}
                </CobaltTableCell>
                {/* issuedOn */}
                <CobaltTableCell>
                  <div className={styles.noWrap}>
                    <DataValue value={issuedOnFormatted} />
                  </div>
                </CobaltTableCell>
                {/* dueDate */}
                <CobaltTableCell>
                  <div className={styles.dueDateCell}>
                    <div className={styles.dueDateFormatted}>
                      <DataValue value={dueDateFormatted} />
                    </div>
                    {shouldShowDueDateIcon(
                      dueDateStatus,
                      DUE_DATE_STATUS.DUE_SOON,
                      item.outstandingAmount,
                      item.balanceStatus
                    ) && (
                      <CobaltIconCautionMajorSolid
                        size="small"
                        className={styles.iconDueSoon}
                        aria-label="Due in the next 7 days"
                      />
                    )}
                    {shouldShowDueDateIcon(
                      dueDateStatus,
                      DUE_DATE_STATUS.PAST_DUE,
                      item.outstandingAmount,
                      item.balanceStatus
                    ) && (
                      <CobaltIconWarningMajorSolid
                        size="small"
                        className={styles.iconPastDue}
                        aria-label="Past due"
                      />
                    )}
                  </div>
                </CobaltTableCell>
                {/* outstandingAmount */}
                <CobaltTableCell className={styles.amountCell}>
                  <OutstandingAmountCell
                    amount={item.outstandingAmount}
                    currency={item.currency}
                    balanceStatus={item.balanceStatus}
                    isPastDue={dueDateStatus === DUE_DATE_STATUS.PAST_DUE}
                  />
                </CobaltTableCell>
                {/* pilotNames */}
                <CobaltTableCell>
                  <TruncatedList
                    items={(item.pilotNames ?? [])
                      .map(formatPilotName)
                      .filter(name => name !== '')}
                    limit={3}
                  />
                </CobaltTableCell>
                {/* schedulerReservationIds */}
                <CobaltTableCell>
                  <TruncatedList
                    items={item.schedulerReservationIds ?? []}
                    limit={2}
                  />
                </CobaltTableCell>
                {/* legalEntityName */}
                <CobaltTableCell>
                  <span className={styles.legalEntityName}>
                    <DataValue value={item.legalEntityName} />
                  </span>
                </CobaltTableCell>
                {/* poNumbers */}
                <CobaltTableCell>
                  <TruncatedList items={item.poNumbers ?? []} limit={2} />
                </CobaltTableCell>
                {canDownload && (
                  <CobaltTableCell>
                    {item.isDownloadAvailable && (
                      <InvoiceDownloadCell
                        accountReceivableId={item.accountReceivableId}
                        transactionNumber={item.transactionNumber || null}
                      />
                    )}
                  </CobaltTableCell>
                )}
              </CobaltTableRow>
            );
          })}
        </CobaltTableBody>
      </CobaltTable>
    </CobaltTableContainer>
  );
});

InvoicesTable.displayName = 'InvoicesTable';

export { InvoicesTable };
